import { Text } from "@solomon-labs/design/components/ui";
import React, { useState } from "react";

import { SeekBar } from "../../widgets/primitives/SeekBar";

import { textVariants } from "./text";

interface ValueEditorProps {
    /** Controlled numeric value as a string (matches the draft model). */
    value: string;
    onChange: (next: string) => void;
    /** Asset/unit beside the amount — a string (rendered as text) or a node
     *  such as a `<TokenChip />`. */
    unit: React.ReactNode;
    /**
     * Layout variant.
     * - `"bounded"` (default): a capped amount over a notched bar that sweeps
     *   in on focus (evenly-spaced, click-to-snap); no idle rail. For
     *   issuer-capped values.
     * - `"unbounded"`: an uncapped amount typed directly, no preset
     *   affordance. For client-set (delegated) values with no issuer cap.
     */
    variant?: "bounded" | "unbounded";
    /** Lower bound — no values below this (default 0). */
    min?: number;
    /** Upper bound for the bounded bar (ignored when `unbounded`). */
    max?: number;
    /** bounded: number of even notch divisions on the bar (default 5). */
    divisions?: number;
    /** bounded: explicit notch values (overrides `divisions`) — each is
     *  positioned by its true value within [min, max]. */
    notchValues?: number[];
    /** Formats the bar's notch labels. */
    formatValue?: (n: number) => string;
    /** Disables typing + bar interaction. */
    disabled?: boolean;
    /** Error state — paints the amount + rail red. Validation lives in the
     *  parent; the editor never clamps, so values can go empty/out-of-range. */
    invalid?: boolean;
    /** Extra classes on the root — set the editable width here
     *  (e.g. "max-w-xl", "w-80", "w-full"). */
    className?: string;
}

/**
 * ValueEditor — a swap-style editable amount (the value *is* its own
 * display) with a `<TokenChip>`-style unit. In the `bounded` variant a
 * `SeekBar` sweeps in on focus, its tick sections evenly-spaced
 * quick-select presets (click-to-snap, no idle rail); `unbounded` is the
 * bare amount for uncapped (delegated) values.
 *
 * Generic + domain-agnostic — the consumer passes bounds/unit/formatter;
 * the component knows nothing about policies. Variant taxonomy (bounded ·
 * unbounded · segmented · split) is still being discovered.
 */
export function ValueEditor({
    value,
    onChange,
    unit,
    variant = "bounded",
    min = 0,
    max = 0,
    divisions = 5,
    notchValues,
    formatValue = (n): string => String(n),
    disabled = false,
    invalid = false,
    className
}: ValueEditorProps): React.ReactElement {
    const [focused, setFocused] = useState(false);
    const isBounded = variant === "bounded";

    const clamp = (n: number): number => {
        const lo = Math.max(min, n);
        return isBounded ? Math.min(max, lo) : lo;
    };

    // Bounded bar geometry — evenly-spaced notches spanning min → max.
    const toPct = (n: number): number =>
        max > min ? ((clamp(n) - min) / (max - min)) * 100 : 0;
    const steps = Math.max(1, Math.round(divisions));
    const notchVals =
        notchValues ??
        Array.from({ length: steps + 1 }, (_, i) =>
            Math.round(min + (i / steps) * (max - min))
        );
    const notches = notchVals.map((v) => ({
        value: v,
        label: formatValue(v),
        pct: toPct(v)
    }));
    const handleSeek = (tickPct: number): void => {
        const notch = notches.find((n) => Math.abs(n.pct - tickPct) < 0.001);
        onChange(
            String(
                clamp(notch ? notch.value : min + (tickPct / 100) * (max - min))
            )
        );
    };

    return (
        <div className={`flex flex-col gap-2 ${className ?? ""}`}>
            {/* Large editable amount — the value is its own display. */}
            <div className="flex items-center gap-2 px-2">
                <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="0"
                    value={value}
                    disabled={disabled}
                    onChange={(e) =>
                        onChange(e.target.value.replace(/\D/g, ""))
                    }
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    className={`${textVariants({ variant: "number-lg" })} h-9 md:h-10 leading-none align-middle min-w-0 flex-1 bg-transparent ${
                        invalid ? "text-error-base" : "text-foreground-base"
                    } placeholder-line-base outline-none disabled:cursor-default`}
                />
                {typeof unit === "string" ? (
                    <Text
                        variant="number-lg"
                        render={<span />}
                        className="shrink-0 text-foreground-muted"
                    >
                        {unit}
                    </Text>
                ) : (
                    unit
                )}
            </div>

            {/* Bounded only: clickable notched bar that sweeps in on focus
                (no idle rail); its sections are evenly-spaced quick-selects. */}
            {isBounded ? (
                <div className="pt-1">
                    <SeekBar
                        pct={toPct(Number(value) || 0)}
                        focused={focused}
                        ticks={notches.map((n) => n.pct)}
                        onSeek={disabled ? undefined : handleSeek}
                    />
                    <div
                        className="relative mt-2 h-4"
                        aria-hidden={!focused}
                        style={{
                            opacity: focused ? 1 : 0,
                            transition: "opacity 200ms ease-out"
                        }}
                    >
                        {notches.map((n, i) => {
                            const align =
                                i === 0
                                    ? "translate-x-0"
                                    : i === notches.length - 1
                                      ? "-translate-x-full"
                                      : "-translate-x-1/2";
                            return (
                                <Text
                                    key={n.value}
                                    variant="fine-print"
                                    render={
                                        <button
                                            type="button"
                                            onMouseDown={(e) =>
                                                e.preventDefault()
                                            }
                                            onClick={() =>
                                                onChange(String(clamp(n.value)))
                                            }
                                        />
                                    }
                                    className={`absolute ${align} cursor-pointer text-foreground-neutral transition-colors hover:text-foreground-base`}
                                    style={{ left: `${n.pct}%` }}
                                >
                                    {n.label}
                                </Text>
                            );
                        })}
                    </div>
                </div>
            ) : (
                /* Unbounded — a plain divider rule under the amount,
                   matching the bounded bar's rail. */
                <div className="pt-1">
                    <div
                        className={`h-px ${
                            invalid ? "bg-error-base" : "bg-line-base"
                        }`}
                    />
                </div>
            )}
        </div>
    );
}
